import {
  type ApiCustomProduct,
  type CrudQuery,
  EditCustomizationArgs,
  type PaginationResponse,
  formatStoreProductData,
  normalizeStoreAlias,
} from "shared-components";

import { CustomFilterOptions } from "@/interfaces/productFilters";

import { actionHandler } from "../action-handler";
import { clientGet, clientPost, clientPut } from "../client";
import type {
  HomeProducts,
  ICustomProductResponse,
  IProductResponse,
  ProductShipping,
  ProductShippingRequest,
} from "./models";

export const getProductsFromStore = actionHandler(
  async ({ id, ...payload }: CrudQuery & { id: string }): Promise<PaginationResponse<IProductResponse>> => {
    return clientPost({ path: `product/search/${id}`, payload });
  },
);

export const getSameStoreProducts = actionHandler(
  async (productId: string): Promise<PaginationResponse<IProductResponse>> => {
    return clientGet({ path: `product/same-store/${productId}` });
  },
);

export const getProductsListsForHome = actionHandler(
  async (): Promise<HomeProducts & { error?: { message: string } }> => {
    return clientGet({ path: "product/home" });
  },
);

export const getSimilarProducts = actionHandler(
  async ({ id, ...payload }: CrudQuery & { id: string }): Promise<PaginationResponse<IProductResponse>> => {
    return clientPost({ path: `product/search/similar/${id}`, payload });
  },
);

export const getProduct = actionHandler(async (id: string): Promise<IProductResponse> => {
  const res: ApiCustomProduct = await clientGet({
    path: `product/${id}`,
    returnStatusCode: true,
  });

  return formatStoreProductData(res) as IProductResponse;
});

/** Retorna produto público pertencente à loja identificada pelo alias */
export const getStoreProductByAlias = actionHandler(
  async ({ alias, productId }: { alias: string; productId: string }): Promise<IProductResponse> => {
    const normalizedAlias = normalizeStoreAlias(alias);
    const res: ApiCustomProduct = await clientGet({
      path: `store/public/alias/${encodeURIComponent(normalizedAlias)}/product/${encodeURIComponent(productId)}`,
      returnStatusCode: true,
    });

    return formatStoreProductData(res) as IProductResponse;
  },
);

export const getFiltersOptions = actionHandler(
  async (storeAlias?: string): Promise<Omit<CustomFilterOptions, "themes" | "licensedSubThemes">> => {
    const normalizedStoreAlias = normalizeStoreAlias(storeAlias);

    if (!normalizedStoreAlias) {
      return clientGet({ path: "product/search/options" });
    }

    const query = new URLSearchParams({ storeAlias: normalizedStoreAlias });

    return clientGet({ path: `product/search/options?${query}` });
  },
);

export const getProductShipping = actionHandler(
  async ({ productId, ...payload }: ProductShippingRequest): Promise<ProductShipping> => {
    return clientPost({ path: `product/shipping/${productId}`, payload });
  },
);

// MÉTODOS PARA PRODUTOS CUSTOMIZADOS PELO USUÁRIO

export const getCustomProduct = actionHandler(async (productId: string): Promise<ICustomProductResponse> => {
  const res: ApiCustomProduct = await clientGet({
    path: `custom-product/${productId}`,
    returnStatusCode: true,
  });

  return formatStoreProductData(res) as ICustomProductResponse;
});

export const updateProductCustomization = actionHandler(
  async ({
    productId,
    ...payload
  }: EditCustomizationArgs & {
    productId: string;
  }): Promise<any> => {
    return clientPut({
      path: `custom-product/${productId}`,
      payload,
    });
  },
);
